export interface ModelDef {
  id: string;
  name: string;
  description: string;
  supportsEdit: boolean;
  supportsCreate: boolean;
  costPerImage: number;
  aspectRatios: string[];
  resolutions: { id: string; label: string; maxSide: number; costMultiplier?: number; autoValue?: string; aspectW?: number; aspectH?: number }[];
  /** FAL endpoint IDs keyed by mode */
  falEndpoints: { edit?: string; create?: string };
  /** How the model accepts sizing: "imageSize" sends {image_size}, "aspectRatio" sends {aspect_ratio, resolution} */
  inputSizingStyle: "imageSize" | "aspectRatio";
  hasSafetyChecker: boolean;
  hasEnhancePrompt: boolean;
  /** Max images this model can generate per request (1 = no slider shown) */
  maxImages: number;
  /** Whether the model supports web search grounding */
  hasWebSearch: boolean;
  /** Additional cost per image when web search is enabled */
  webSearchCostPerImage: number;
  /** Whether the model uses safety_tolerance (1-6) instead of enable_safety_checker */
  hasSafetyTolerance?: boolean;
  /** Whether the model supports limit_generations parameter */
  hasLimitGenerations?: boolean;
  /** Whether this model is an upscaler (image-to-image enhancement) */
  isUpscaler?: boolean;
  /** Whether the upscaler supports target resolution mode (e.g. SeedVR) */
  hasTargetResolution?: boolean;
  /** Available target resolutions for upscaler models */
  upscaleTargetResolutions?: { id: string; label: string; estimatedCost: number }[];
  /** Whether the upscaler supports noise scale */
  hasNoiseScale?: boolean;
  /** Available upscaler model variants (e.g. Topaz enhancement models) */
  upscalerVariants?: { id: string; label: string }[];
  /** Whether the upscaler supports face enhancement */
  hasFaceEnhancement?: boolean;
  /** Whether the upscaler supports subject detection modes */
  hasSubjectDetection?: boolean;
  /** Max upscale factor for upscaler models */
  maxUpscaleFactor?: number;
  /** Available output formats for upscaler models */
  outputFormats?: { id: string; label: string }[];
  /** Whether the model supports negative_prompt */
  hasNegativePrompt?: boolean;
  /** Default negative prompt to pre-fill */
  defaultNegativePrompt?: string;
  /** Whether the model supports output_format for non-upscaler generation */
  hasOutputFormat?: boolean;
  /** Available output formats for generation (non-upscaler) */
  generationOutputFormats?: { id: string; label: string }[];
  /** Whether the model uses enable_prompt_expansion instead of enhance_prompt_mode */
  hasPromptExpansion?: boolean;
  /** Force input image upload format (e.g. PNG for OpenAI models) */
  inputImageFormat?: "png";
  /** Whether the model accepts a seed input parameter */
  hasSeed?: boolean;
  /** Whether the model needs a hand-drawn black-and-white mask alongside a single source image */
  requiresMask?: boolean;
  /** Whether the model supports a quality parameter */
  hasQuality?: boolean;
  /** Available quality options when hasQuality is true */
  qualityOptions?: { id: string; label: string }[];
}

export const MODELS: ModelDef[] = [
  {
    id: "seedream-v5-lite",
    name: "Seedream v5 Lite",
    description: "Bytedance",
    supportsEdit: true,
    supportsCreate: true,
    costPerImage: 0.035,
    aspectRatios: ["1:1", "3:4", "4:3", "2:3", "3:2", "9:16", "16:9"],
    resolutions: [
      { id: "auto_2K", label: "2K Auto", maxSide: 2048, autoValue: "auto_2K" },
      { id: "auto_3K", label: "3K Auto", maxSide: 3072, autoValue: "auto_3K" },
    ],
    falEndpoints: {
      edit: "fal-ai/bytedance/seedream/v5/lite/edit",
      create: "fal-ai/bytedance/seedream/v5/lite/text-to-image",
    },
    inputSizingStyle: "imageSize",
    hasSafetyChecker: true,
    hasEnhancePrompt: true,
    maxImages: 6,
    hasWebSearch: false,
    webSearchCostPerImage: 0,
    hasOutputFormat: true,
    generationOutputFormats: [
      { id: "jpeg", label: "JPEG" },
      { id: "png", label: "PNG" },
    ],
  },
  {
    id: "seedream-v5-pro",
    name: "Seedream v5 Pro",
    description: "Bytedance",
    supportsEdit: true,
    supportsCreate: false,
    costPerImage: 0.0675,
    aspectRatios: ["1:1", "3:4", "4:3", "2:3", "3:2", "9:16", "16:9"],
    resolutions: [
      { id: "auto_1K", label: "1K Auto", maxSide: 1024, autoValue: "auto_1K" },
      { id: "auto_2K", label: "2K Auto", maxSide: 2048, autoValue: "auto_2K", costMultiplier: 2 },
    ],
    falEndpoints: {
      edit: "bytedance/seedream/v5/pro/edit",
    },
    inputSizingStyle: "imageSize",
    hasSafetyChecker: true,
    hasEnhancePrompt: false,
    maxImages: 6,
    hasWebSearch: false,
    webSearchCostPerImage: 0,
    hasOutputFormat: true,
    generationOutputFormats: [
      { id: "jpeg", label: "JPEG" },
      { id: "png", label: "PNG" },
    ],
  },
  {
    id: "imagen4-ultra",
    name: "Imagen 4 Ultra",
    description: "Google",
    supportsEdit: false,
    supportsCreate: true,
    costPerImage: 0.06,
    aspectRatios: ["1:1", "4:3", "3:4", "16:9", "9:16"],
    resolutions: [
      { id: "1K", label: "1K", maxSide: 1024 },
      { id: "2K", label: "2K", maxSide: 2048 },
    ],
    falEndpoints: {
      create: "fal-ai/imagen4/preview/ultra",
    },
    inputSizingStyle: "aspectRatio",
    hasSafetyChecker: false,
    hasEnhancePrompt: false,
    maxImages: 1,
    hasWebSearch: false,
    webSearchCostPerImage: 0,
    hasSeed: true,
    hasOutputFormat: true,
    generationOutputFormats: [
      { id: "jpeg", label: "JPEG" },
      { id: "png", label: "PNG" },
    ],
  },
  {
    id: "nano-banana-pro",
    name: "Nano Banana Pro",
    description: "Google",
    supportsEdit: true,
    supportsCreate: true,
    costPerImage: 0.15,
    aspectRatios: ["1:1", "21:9", "16:9", "3:2", "4:3", "5:4", "4:5", "3:4", "2:3", "9:16"],
    resolutions: [
      { id: "1K", label: "1K", maxSide: 1024 },
      { id: "2K", label: "2K", maxSide: 2048 },
      { id: "4K", label: "4K", maxSide: 4096, costMultiplier: 2 },
    ],
    falEndpoints: {
      edit: "fal-ai/nano-banana-pro/edit",
      create: "fal-ai/nano-banana-pro",
    },
    inputSizingStyle: "aspectRatio",
    hasSafetyChecker: false,
    hasSafetyTolerance: true,
    hasEnhancePrompt: false,
    maxImages: 4,
    hasWebSearch: true,
    webSearchCostPerImage: 0.015,
    hasSeed: true,
    hasOutputFormat: true,
    generationOutputFormats: [
      { id: "jpeg", label: "JPEG" },
      { id: "png", label: "PNG" },
    ],
  },
  {
    id: "nano-banana-2",
    name: "Nano Banana 2",
    description: "Google",
    supportsEdit: true,
    supportsCreate: true,
    costPerImage: 0.08,
    aspectRatios: ["auto", "1:1", "21:9", "16:9", "3:2", "4:3", "5:4", "4:5", "3:4", "2:3", "9:16"],
    resolutions: [
      { id: "0.5K", label: "0.5K", maxSide: 512, costMultiplier: 0.75 },
      { id: "1K", label: "1K", maxSide: 1024 },
      { id: "2K", label: "2K", maxSide: 2048, costMultiplier: 1.5 },
      { id: "4K", label: "4K", maxSide: 4096, costMultiplier: 2 },
    ],
    falEndpoints: {
      edit: "fal-ai/nano-banana-2/edit",
      create: "fal-ai/nano-banana-2",
    },
    inputSizingStyle: "aspectRatio",
    hasSafetyChecker: false,
    hasSafetyTolerance: true,
    hasEnhancePrompt: false,
    maxImages: 4,
    hasWebSearch: true,
    webSearchCostPerImage: 0.015,
    hasLimitGenerations: true,
    hasSeed: true,
    hasOutputFormat: true,
    generationOutputFormats: [
      { id: "jpeg", label: "JPEG" },
      { id: "png", label: "PNG" },
    ],
  },
  {
    id: "nano-banana-2-lite",
    name: "Nano Banana 2 Lite",
    description: "Google",
    supportsEdit: true,
    supportsCreate: true,
    costPerImage: 0.05,
    aspectRatios: ["auto", "1:1", "21:9", "16:9", "3:2", "4:3", "5:4", "4:5", "3:4", "2:3", "9:16", "4:1", "1:4", "8:1", "1:8"],
    resolutions: [
      { id: "1K", label: "1K", maxSide: 1024 },
    ],
    falEndpoints: {
      edit: "google/nano-banana-lite/edit",
      create: "google/nano-banana-2-lite",
    },
    inputSizingStyle: "aspectRatio",
    hasSafetyChecker: false,
    hasSafetyTolerance: true,
    hasEnhancePrompt: false,
    maxImages: 4,
    hasWebSearch: false,
    webSearchCostPerImage: 0,
    hasLimitGenerations: true,
    hasSeed: true,
    hasOutputFormat: true,
    generationOutputFormats: [
      { id: "png", label: "PNG" },
      { id: "jpeg", label: "JPEG" },
      { id: "webp", label: "WebP" },
    ],
  },
  {
    id: "qwen-image-2",
    name: "Qwen Image 2",
    description: "Qwen",
    supportsEdit: true,
    supportsCreate: true,
    costPerImage: 0.035,
    aspectRatios: ["1:1", "4:3", "3:4", "16:9", "9:16", "3:2", "2:3"],
    resolutions: [
      { id: "1K", label: "1K", maxSide: 1024 },
      { id: "2K", label: "2K", maxSide: 2048 },
      { id: "4K", label: "4K", maxSide: 4096, costMultiplier: 2 },
    ],
    falEndpoints: {
      edit: "fal-ai/qwen-image-2/edit",
      create: "fal-ai/qwen-image-2/text-to-image",
    },
    inputSizingStyle: "imageSize",
    hasSafetyChecker: true,
    hasEnhancePrompt: false,
    hasPromptExpansion: true,
    maxImages: 6,
    hasWebSearch: false,
    webSearchCostPerImage: 0,
    hasNegativePrompt: true,
    defaultNegativePrompt: "low resolution, error, worst quality, low quality, deformed",
    hasSeed: true,
    hasOutputFormat: true,
    generationOutputFormats: [
      { id: "jpeg", label: "JPEG" },
      { id: "png", label: "PNG" },
      { id: "webp", label: "WebP" },
    ],
  },
  {
    id: "qwen-image-2-pro",
    name: "Qwen Image 2 Pro",
    description: "Qwen",
    supportsEdit: true,
    supportsCreate: true,
    costPerImage: 0.075,
    aspectRatios: ["1:1", "4:3", "3:4", "16:9", "9:16", "3:2", "2:3"],
    resolutions: [
      { id: "1K", label: "1K", maxSide: 1024 },
      { id: "2K", label: "2K", maxSide: 2048 },
      { id: "4K", label: "4K", maxSide: 4096, costMultiplier: 2 },
    ],
    falEndpoints: {
      edit: "fal-ai/qwen-image-2/pro/edit",
      create: "fal-ai/qwen-image-2/pro/text-to-image",
    },
    inputSizingStyle: "imageSize",
    hasSafetyChecker: true,
    hasEnhancePrompt: false,
    hasPromptExpansion: true,
    maxImages: 6,
    hasWebSearch: false,
    webSearchCostPerImage: 0,
    hasNegativePrompt: true,
    defaultNegativePrompt: "low resolution, error, worst quality, low quality, deformed",
    hasSeed: true,
    hasOutputFormat: true,
    generationOutputFormats: [
      { id: "jpeg", label: "JPEG" },
      { id: "png", label: "PNG" },
      { id: "webp", label: "WebP" },
    ],
  },
  {
    id: "wan-2-7-pro",
    name: "Wan 2.7 Pro",
    description: "Wan",
    supportsEdit: true,
    supportsCreate: true,
    costPerImage: 0.075,
    aspectRatios: ["1:1", "3:4", "4:3", "16:9", "9:16"],
    resolutions: [
      { id: "1K", label: "1K", maxSide: 1024 },
      { id: "2K", label: "2K", maxSide: 2048 },
      { id: "4K", label: "4K", maxSide: 4096, costMultiplier: 2 },
    ],
    falEndpoints: {
      create: "fal-ai/wan/v2.7/pro/text-to-image",
      edit: "fal-ai/wan/v2.7/pro/edit",
    },
    inputSizingStyle: "aspectRatio",
    hasSafetyChecker: true,
    hasEnhancePrompt: false,
    hasPromptExpansion: true,
    maxImages: 4,
    hasWebSearch: false,
    webSearchCostPerImage: 0,
    hasNegativePrompt: true,
    defaultNegativePrompt: "",
    hasSeed: true,
  },
  {
    id: "gpt-image-2",
    name: "GPT Image 2",
    description: "OpenAI",
    supportsEdit: true,
    supportsCreate: false,
    costPerImage: 0.19,
    aspectRatios: [],
    resolutions: [
      { id: "auto", label: "Auto", maxSide: 1024, autoValue: "auto", aspectW: 1, aspectH: 1 },
      { id: "square_hd", label: "1024²", maxSide: 1024, autoValue: "square_hd", aspectW: 1, aspectH: 1 },
      { id: "square", label: "512²", maxSide: 512, autoValue: "square", aspectW: 1, aspectH: 1 },
      { id: "portrait_4_3", label: "768×1024", maxSide: 1024, autoValue: "portrait_4_3", aspectW: 3, aspectH: 4 },
      { id: "portrait_16_9", label: "576×1024", maxSide: 1024, autoValue: "portrait_16_9", aspectW: 9, aspectH: 16 },
      { id: "landscape_4_3", label: "1024×768", maxSide: 1024, autoValue: "landscape_4_3", aspectW: 4, aspectH: 3 },
      { id: "landscape_16_9", label: "1024×576", maxSide: 1024, autoValue: "landscape_16_9", aspectW: 16, aspectH: 9 },
    ],
    falEndpoints: {
      edit: "openai/gpt-image-2/edit",
    },
    inputSizingStyle: "imageSize",
    hasSafetyChecker: false,
    hasEnhancePrompt: false,
    maxImages: 4,
    hasWebSearch: false,
    webSearchCostPerImage: 0,
    inputImageFormat: "png",
    hasQuality: true,
    qualityOptions: [
      { id: "auto", label: "Auto" },
      { id: "low", label: "Low" },
      { id: "medium", label: "Medium" },
      { id: "high", label: "High" },
    ],
    hasOutputFormat: true,
    generationOutputFormats: [
      { id: "png", label: "PNG" },
      { id: "jpeg", label: "JPEG" },
      { id: "webp", label: "WebP" },
    ],
  },
  {
    id: "gpt-image-2-5-sunburst",
    name: "GPT Image 2.5 Sunburst",
    description: "OpenAI",
    supportsEdit: true,
    supportsCreate: false,
    costPerImage: 0.19,
    aspectRatios: [],
    resolutions: [
      { id: "auto", label: "Auto", maxSide: 1024, autoValue: "auto", aspectW: 1, aspectH: 1 },
      { id: "square_hd", label: "1024²", maxSide: 1024, autoValue: "square_hd", aspectW: 1, aspectH: 1 },
      { id: "square", label: "512²", maxSide: 512, autoValue: "square", aspectW: 1, aspectH: 1 },
      { id: "portrait_4_3", label: "768×1024", maxSide: 1024, autoValue: "portrait_4_3", aspectW: 3, aspectH: 4 },
      { id: "portrait_16_9", label: "576×1024", maxSide: 1024, autoValue: "portrait_16_9", aspectW: 9, aspectH: 16 },
      { id: "landscape_4_3", label: "1024×768", maxSide: 1024, autoValue: "landscape_4_3", aspectW: 4, aspectH: 3 },
      { id: "landscape_16_9", label: "1024×576", maxSide: 1024, autoValue: "landscape_16_9", aspectW: 16, aspectH: 9 },
    ],
    falEndpoints: {
      edit: "openai/gpt-image-2.5/sunburst/edit",
    },
    inputSizingStyle: "imageSize",
    hasSafetyChecker: false,
    hasEnhancePrompt: false,
    maxImages: 4,
    hasWebSearch: false,
    webSearchCostPerImage: 0,
    inputImageFormat: "png",
    hasQuality: true,
    qualityOptions: [
      { id: "auto", label: "Auto" },
      { id: "low", label: "Low" },
      { id: "medium", label: "Medium" },
      { id: "high", label: "High" },
      { id: "xhigh", label: "X-High" },
      { id: "max", label: "Max" },
    ],
    hasOutputFormat: true,
    generationOutputFormats: [
      { id: "png", label: "PNG" },
      { id: "jpeg", label: "JPEG" },
      { id: "webp", label: "WebP" },
    ],
  },
  {
    id: "gpt-image-2-5-flare",
    name: "GPT Image 2.5 Flare",
    description: "OpenAI",
    supportsEdit: true,
    supportsCreate: false,
    costPerImage: 0.19,
    aspectRatios: [],
    resolutions: [
      { id: "auto", label: "Auto", maxSide: 1024, autoValue: "auto", aspectW: 1, aspectH: 1 },
      { id: "square_hd", label: "1024²", maxSide: 1024, autoValue: "square_hd", aspectW: 1, aspectH: 1 },
      { id: "square", label: "512²", maxSide: 512, autoValue: "square", aspectW: 1, aspectH: 1 },
      { id: "portrait_4_3", label: "768×1024", maxSide: 1024, autoValue: "portrait_4_3", aspectW: 3, aspectH: 4 },
      { id: "portrait_16_9", label: "576×1024", maxSide: 1024, autoValue: "portrait_16_9", aspectW: 9, aspectH: 16 },
      { id: "landscape_4_3", label: "1024×768", maxSide: 1024, autoValue: "landscape_4_3", aspectW: 4, aspectH: 3 },
      { id: "landscape_16_9", label: "1024×576", maxSide: 1024, autoValue: "landscape_16_9", aspectW: 16, aspectH: 9 },
    ],
    falEndpoints: {
      edit: "openai/gpt-image-2.5/flare/edit",
    },
    inputSizingStyle: "imageSize",
    hasSafetyChecker: false,
    hasEnhancePrompt: false,
    maxImages: 4,
    hasWebSearch: false,
    webSearchCostPerImage: 0,
    inputImageFormat: "png",
    hasQuality: true,
    qualityOptions: [
      { id: "auto", label: "Auto" },
      { id: "low", label: "Low" },
      { id: "medium", label: "Medium" },
      { id: "high", label: "High" },
      { id: "xhigh", label: "X-High" },
      { id: "max", label: "Max" },
    ],
    hasOutputFormat: true,
    generationOutputFormats: [
      { id: "png", label: "PNG" },
      { id: "jpeg", label: "JPEG" },
      { id: "webp", label: "WebP" },
    ],
  },
  {
    id: "ideogram-object-removal",
    name: "Object Removal",
    description: "Ideogram",
    supportsEdit: true,
    supportsCreate: false,
    costPerImage: 0.03,
    aspectRatios: [],
    resolutions: [],
    falEndpoints: {
      edit: "fal-ai/ideogram/object-removal",
    },
    inputSizingStyle: "imageSize",
    hasSafetyChecker: false,
    hasEnhancePrompt: false,
    maxImages: 1,
    hasWebSearch: false,
    webSearchCostPerImage: 0,
    requiresMask: true,
  },
  {
    id: "seedvr2",
    name: "SeedVR2",
    description: "Seamless Upscaler",
    supportsEdit: true,
    supportsCreate: false,
    costPerImage: 0.0025,
    aspectRatios: [],
    resolutions: [],
    falEndpoints: {
      edit: "fal-ai/seedvr/upscale/image/seamless",
    },
    inputSizingStyle: "imageSize",
    hasSafetyChecker: false,
    hasEnhancePrompt: false,
    maxImages: 1,
    hasWebSearch: false,
    webSearchCostPerImage: 0,
    isUpscaler: true,
    hasTargetResolution: true,
    upscaleTargetResolutions: [
      { id: "720p", label: "720p", estimatedCost: 0.001 },
      { id: "1080p", label: "1080p", estimatedCost: 0.002 },
      { id: "1440p", label: "1440p", estimatedCost: 0.004 },
      { id: "2160p", label: "2160p", estimatedCost: 0.008 },
    ],
    hasNoiseScale: true,
    maxUpscaleFactor: 10,
    outputFormats: [
      { id: "jpeg", label: "JPEG" },
      { id: "png", label: "PNG" },
      { id: "webp", label: "WebP" },
    ],
  },
  {
    id: "topaz",
    name: "Topaz",
    description: "Upscaler",
    supportsEdit: true,
    supportsCreate: false,
    costPerImage: 0.08,
    aspectRatios: [],
    resolutions: [],
    falEndpoints: {
      edit: "fal-ai/topaz/upscale/image",
    },
    inputSizingStyle: "imageSize",
    hasSafetyChecker: false,
    hasEnhancePrompt: false,
    maxImages: 1,
    hasWebSearch: false,
    webSearchCostPerImage: 0,
    isUpscaler: true,
    upscalerVariants: [
      { id: "Standard V2", label: "Standard V2" },
      { id: "Low Resolution V2", label: "Low Res V2" },
      { id: "CGI", label: "CGI" },
      { id: "High Fidelity V2", label: "High Fidelity V2" },
      { id: "Text Refine", label: "Text Refine" },
      { id: "Recovery", label: "Recovery" },
      { id: "Redefine", label: "Redefine" },
      { id: "Recovery V2", label: "Recovery V2" },
    ],
    hasFaceEnhancement: true,
    hasSubjectDetection: true,
    maxUpscaleFactor: 4,
    outputFormats: [
      { id: "jpeg", label: "JPEG" },
      { id: "png", label: "PNG" },
    ],
  },
];

export function getModelDef(modelId: string): ModelDef {
  return MODELS.find((m) => m.id === modelId) ?? MODELS[0];
}

export function getModelDisplayName(modelId: string): string {
  return MODELS.find((m) => m.id === modelId)?.name ?? modelId;
}

export function resolveEndpoint(modelId: string, mode: "edit" | "create"): string {
  const model = getModelDef(modelId);
  const endpoint = model.falEndpoints[mode];
  if (!endpoint) {
    throw new Error(`Model "${model.name}" does not support ${mode} mode`);
  }
  return endpoint;
}
